<template>
    <div class="component component__modules_item_session">
        <header class="component__modules_item_session_header">
            <h2 class="component__modules_item_session_header_name">{{ eventName }}</h2>
        </header>
        <div class="component__modules_item_session_content">
            <ul>
                <li
                    v-for="(occurrence, index) in sessionOccurrenceItems"
                    :key="index"
                >
                    <div class="component__modules_item_session_content_time">
                        <div class="columns is-vcentered">
                            <template v-if="toISODate(occurrence.datetimeStart) !== toISODate(occurrence.datetimeEnd)">
                                <div class="column is-narrow">
                                    <div class="component__modules_item_session_content_time_date">
                                        {{ toLocaleDateTime(occurrence.datetimeStart) }}
                                    </div>
                                    <div class="component__modules_item_session_content_time_hours">
                                        <v-icon name="clock"/>
                                        {{ fromUTCTimeToLocal(occurrence.datetimeStart) }}
                                    </div>
                                </div>
                                <div class="column has-text-centered">
                                    <div class="component__modules_item_session_content_time_separator">-</div>
                                </div>
                                <div class="column is-narrow">
                                    <div class="component__modules_item_session_content_time_date">
                                        {{ toLocaleDateTime(occurrence.datetimeEnd) }}
                                    </div>
                                    <div class="component__modules_item_session_content_time_hours">
                                        <v-icon name="clock"/>
                                        {{ fromUTCTimeToLocal(occurrence.datetimeEnd) }}
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="column is-narrow">
                                    <div class="component__modules_item_session_content_time_date">
                                        {{ toLocaleDateTime(occurrence.datetimeStart) }}
                                    </div>
                                </div>
                                <div class="column"></div>
                                <div class="column is-narrow">
                                    <div class="component__modules_item_session_content_time_hours">
                                        <v-icon name="clock"/>
                                        {{ fromUTCTimeToLocal(occurrence.datetimeStart) }} - {{ fromUTCTimeToLocal(occurrence.datetimeEnd) }}
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                    <div class="component__modules_item_session_content_details">
                        <ul>
                            <li>
                                <div class="columns is-vcentered">
                                    <div class="column is-narrow">
                                        <div class="component__modules_item_session_content_details_icon">
                                            <v-icon name="map-pin"/>
                                        </div>
                                    </div>
                                    <div class="column">
                                        <div class="component__modules_item_session_content_details_desc">
                                            <template v-if="occurrence.roomId">
                                                {{ occurrence.room.name }}, {{ occurrence.venue.name }}
                                            </template>
                                            <template v-else>
                                                {{ occurrence.platform }}
                                            </template>
                                        </div>
                                    </div>
                                </div>
                            </li>
                            <template v-if="occurrence.trainers.length">
                                <li>
                                    <div class="columns is-vcentered">
                                        <div class="column is-narrow">
                                            <div class="component__modules_item_session_content_details_icon">
                                                <v-icon name="clipboard"/>
                                            </div>
                                        </div>
                                        <div class="column">
                                            <div class="component__modules_item_session_content_details_desc">
                                                <ul>
                                                    <li
                                                        v-for="(trainer, trainerIndex) in occurrence.trainers"
                                                        :key="trainerIndex"
                                                    >{{ trainer.name }}
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            </template>
                        </ul>
                    </div>
                </li>
            </ul>
        </div>
        <footer class="component__modules_item_session_footer">
            <div class="columns is-vcentered">
                <div class="column is-vbottom">
                    <div class="component__modules_item_session_footer_spaces">
                        <div class="columns is-variable is-2 is-vcentered">
                            <div class="column is-narrow">
                                <div class="component__modules_item_session_footer_spaces_icon">
                                    <v-icon name="users"/>
                                </div>
                            </div>
                            <div class="column">
                                <div class="component__modules_item_session_footer_spaces_desc">
                                    {{ sessionAvailableSpaces }}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="component__modules_item_session_footer_tags">
                        <div class="columns is-variable">
                            <template v-if="user.isLearner && isUserEnrolled">
                                <div class="column is-narrow">
                                    <component-status :status="sessionUserBookingStatus"></component-status>
                                </div>
                            </template>
                            <template v-if="user.isEventCoordinator()">
                                <div class="column is-narrow">
                                    <component-status :status="sessionStatus"></component-status>
                                </div>
                            </template>
                        </div>
                    </div>
                    <div class="component__modules_item_session_footer_actions">
                        <div class="columns is-variable">
                            <template v-if="user.isLearner && isUserEnrolled && routeIsCourseVersionView">
                                <div class="column is-narrow">
                                    <template v-if="requestValidateBookingPending">
                                        <component-loading></component-loading>
                                    </template>
                                    <template v-else>
                                        <component-button
                                            v-if="!allowForSelfCancelBooking"
                                            :name="'component-modules-item-session-book'"
                                            :disabled="!allowForBooking"
                                            :disabled-hint-position="'top'"
                                            :disabled-hint-active="!allowForBooking"
                                            :disabled-hint-title="$t('ebs.sessions.show.title.unableBecause')"
                                            :disabled-hint-items="allowForBookingErrors.errors"
                                            :disabled-hint-extra-title="allowForBookingErrors.errorsExtra ? allowForBookingErrors.errorsExtra.length ? $t('ebs.sessions.show.title.please') : '' : ''"
                                            :disabled-hint-extra-items="allowForBookingErrors.errorsExtra"
                                            @click="openBookNotify"
                                        >Book
                                        </component-button>
                                        <component-button
                                            v-if="allowForSelfCancelBooking"
                                            :theme="'default'"
                                            :border="true"
                                            :disabled="!sessionBookingInfoCancellationAllowed"
                                            :name="'session-show-booking-actions-cancel'"
                                            @click="openBookCancelNotify"
                                        >Cancel booking
                                        </component-button>
                                    </template>
                                    <pop-notify
                                        v-if="(allowForBooking)"
                                        ref="booknotify"
                                        :class="'pop__notify--booking'"
                                        :title-text="$t('ebs.sessions.show.text.bookOnTo')"
                                        :content-text="sessionBookingFormRequired ? this.$t('ebs.sessions.show.text.pleaseCompleteTheForm') : this.$t('ebs.sessions.show.text.pleaseConfirmYourBooking')"
                                        :form-active="sessionBookingFormRequired"
                                        :form-fields="bookingFormFields"
                                        :cancel-button-text="$t('ebs.sessions.show.buttons.cancel')"
                                        :submit-button-text="$t('ebs.sessions.show.buttons.bookOnto')"
                                        :prevent-close="true"
                                        @emit-form="updateBookingFields"
                                        @emit-submit="createBooking"
                                    />
                                    <pop-notify
                                        v-if="(allowForSelfCancelBooking)"
                                        ref="bookcancelnotify"
                                        :class="'pop__notify--booking'"
                                        :title-text="$t('ebs.sessions.show.buttons.cancelBooking')"
                                        :content-text="$t('ebs.sessions.show.text.areYouSure') + '\n\n' + this.$t('ebs.sessions.show.text.coordinatorWillReceive')"
                                        :cancel-button-text="$t('ebs.sessions.show.buttons.goBack')"
                                        :select-value="bookingCancellationReason"
                                        :select-active="sessionCancellationReasonRequired"
                                        :select-title="$t('ebs.sessions.show.title.cancellationReason')"
                                        :select-items="sessionCancellationReason"
                                        :select-items-other="sessionCancellationReasonOther"
                                        :submit-button-text="$t('ebs.sessions.show.buttons.cancelBooking')"
                                        :submit-button-danger="true"
                                        :prevent-close="true"
                                        @emit-select="updateBookingCancellationReason"
                                        @emit-submit="cancelBooking"
                                    />
                                </div>
                                <div class="column is-narrow">
                                    <component-button-menu
                                        :buttons="[
                                            {
                                                key: 'show-session',
                                                name: 'Show session'
                                            }
                                        ]"
                                        @emit-show-session="redirectToSession"
                                    ></component-button-menu>
                                </div>
                            </template>
                            <template v-else>
                                <div class="column is-narrow">
                                    <component-button
                                        :theme="'default'"
                                        :name="'component-modules-item-session-book'"
                                        :to="{ name: 'ebs-session-show', params: { eventId: eventId, sessionId: sessionId } }"
                                    >Show session
                                    </component-button>
                                </div>
                            </template>
                            <div class="column"></div>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    </div>
</template>

<script>
import ComponentStatus from '@/components/default/shared/ComponentStatus'
import { User } from '@/User'
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentButtonMenu from '@/components/default/shared/ComponentButtonMenu'
import ComponentLoading from '@/components/default/shared/ComponentLoading'
import { setResponseValidateNotification } from '@/notificationsHelpers'
import PopNotify from '@/components/default/shared/PopNotify'
import { maxNumber } from '@/variables'

export default {
  components: { PopNotify, ComponentLoading, ComponentButtonMenu, ComponentButton, ComponentStatus },
  name: 'component-modules-item-session',
  data () {
    return {
      user: new User(),
      allowForBooking: false,
      allowForBookingErrors: {},
      requestValidateBookingPending: false
    }
  },
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  computed: {
    routeIsCourseVersionView () {
      return this.$route.name === 'ebs-course-version-show'
    },
    eventId () {
      return this.item.eventId
    },
    eventName () {
      return this.item.eventName
    },
    sessionId () {
      return this.item.id
    },
    sessionOccurrenceItems () {
      return this.item.occurrenceItems
    },
    sessionStatus () {
      return this.item.status
    },
    sessionUserBookingStatus () {
      return this.item.userBookingStatus
    },
    sessionUserBookingId () {
      return this.item.userBookingId
    },
    sessionAvailableSpaces () {
      if (this.item.spaces.limit === this.item.spaces.booked) {
        return 'No spaces available'
      } else if (this.item.spaces.limit === maxNumber) {
        return `${this.item.spaces.booked} ${this.item.spaces.booked === 1 ? 'person has' : 'people have'} booked`
      } else {
        return `${this.item.spaces.limit - this.item.spaces.booked} / ${this.item.spaces.limit}` + ((this.item.spaces.limit - this.item.spaces.booked) !== 1 ? ` spaces` : ` space`) + ` available`
      }
    },
    sessionBookingFormRequired () {
      return this.item.bookingSettings.bookingFormRequired
    },
    sessionBookingFormFields () {
      return this.item.bookingSettings.bookingForm.fields
    },
    sessionBookingInfoCancellationAllowed () {
      return this.item.bookingInfo.cancellationAllowed
    },
    sessionCancellationReasonRequired () {
      return this.item.cancellationSettings.cancellationReasonRequired
    },
    sessionCancellationReason () {
      const items = this.item.cancellationSettings.cancellationReason
      return this.fromStringToArraySelect(items)
    },
    sessionCancellationReasonOther () {
      return this.item.cancellationSettings.cancellationReasonCustomEnabled
    },
    bookingFormFields () {
      return this.$store.getters.getBookingFields
    },
    bookingCancellationReason () {
      return this.$store.getters.getBookingCancellationReason
    },
    allowForSelfCancelBooking () {
      const sessionStatus = this.sessionStatus
      const bookingStatus = this.sessionUserBookingStatus
      const allowCancel = this.item.cancellationSettings.selfCancellationAllowed

      return (
        sessionStatus === 'upcoming' &&
      bookingStatus === 'booked' &&
      allowCancel
      )
    },
    isUserEnrolled () {
      return this.$store.getters.getEbsEnrolmentIsUserEnrolled
    }
  },
  methods: {
    openBookNotify () {
      this.clearBooking()
      this.$refs.booknotify.openNotify()
      this.setBookingFields()
    },
    openBookCancelNotify () {
      this.getBooking()
      this.$refs.bookcancelnotify.openNotify()
    },
    clearBooking () {
      this.$store.dispatch('clearBooking')
    },
    getBooking () {
      const bookingID = this.sessionUserBookingId
      this.$store.dispatch('getBooking', bookingID)
    },
    setBookingFields () {
      this.$store.dispatch('setBookingFields', { bookingFormFields: JSON.parse(JSON.stringify(this.sessionBookingFormFields)), bookingFormRequired: JSON.parse(JSON.stringify(this.sessionBookingFormRequired)) })
    },
    updateBookingFields (data) {
      this.$store.dispatch('updateBookingFields', data)
    },
    updateBookingCancellationReason (data) {
      this.$store.dispatch('setBookingCancellationReason', data)
    },
    createBooking () {
      this.$store.dispatch('createBooking', { eventId: this.eventId, occurrenceId: this.sessionId }).then(() => {
        this.$refs.booknotify.closeNotify()
        this.$emit('emit-booking')
      })
    },
    cancelBooking () {
      this.$store.dispatch('cancelBooking').then(() => {
        this.$refs.bookcancelnotify.closeNotify()
        this.$emit('emit-booking')
      })
    },
    validateBooking () {
      if (this.isUserEnrolled) {
        this.requestValidateBookingPending = true
        this.$store.dispatch('validateBooking', { eventId: this.eventId, occurrenceId: this.sessionId }).then(() => {
          this.allowForBooking = true
          this.requestValidateBookingPending = false
        }).catch(error => {
          this.allowForBooking = false
          this.allowForBookingErrors = setResponseValidateNotification(error)
          this.requestValidateBookingPending = false
        })
      }
    },
    redirectToSession () {
      this.$router.push({ name: 'ebs-session-show', params: { eventId: this.eventId, sessionId: this.sessionId } })
    }
  },
  mounted () {
    this.validateBooking()
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/default/shared/componentmodulesitemsession";
</style>
